import {call, put, takeLatest} from 'redux-saga/effects';
import influencerActions from '../actions/influencer.actions';
import trackingActions, {EVENT_ACTION, EVENT_CATEGORY} from '../actions/tracking.actions';
import InfluencerApi from '../api/InfluencerApi';
import errorActions from '../actions/error.actions';
import {handleSagaError} from './utils/CommonUtils';

function* getInfluencerProfile(action) {
    try {
        const result = yield call(InfluencerApi.getInfluencerProfile, action.payload);
        yield put(influencerActions.getInfluencerProfileSuccess(result));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.getInfluencerProfileFailed(ex));
    }
}

function* createInfluencerProfile(action) {
    try {
        yield put(errorActions.hideErrorMessage());
        const result = yield call(InfluencerApi.createInfluencerProfile, action.payload);
        yield put(influencerActions.createInfluencerProfileSuccess(result));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.createInfluencerProfileFailed(ex));
    }
}

function* reactivateInfluencerProfile(action) {
    try {
        yield put(errorActions.hideErrorMessage());
        const result = yield call(InfluencerApi.reactivateInfluencerProfile, action.payload);
        yield put(influencerActions.reactivateInfluencerProfileSuccess(result));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.reactivateInfluencerProfileFailed(ex));
    }
}

function* getSocialChannels(action) {
    try {
        const result = yield call(InfluencerApi.getSocialChannels, action.payload);
        yield put(influencerActions.getSocialChannelsSuccess(result));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.getSocialChannelsFailed(ex));
    }
}

function* updateMainChannel(action) {
    try {
        const result = yield call(InfluencerApi.updateMainChannel, action.payload);
        yield put(influencerActions.updateMainChannelSuccess(result));
        yield put(
            trackingActions.trackInteractionEvent(
                EVENT_CATEGORY.SocialChannel,
                EVENT_ACTION.UpdateMainChannel,
                action.payload
            )
        );
    } catch (ex) {
        yield call(getSocialChannels, {});
        yield handleSagaError(ex, influencerActions.updateMainChannelFailed(ex));
    }
}

function* resendEmailVerification() {
    try {
        const result = yield call(InfluencerApi.resendEmailVerification, {});
        if (result.success === false) {
            yield put(errorActions.showErrorMessage(result));
            yield put(influencerActions.resendEmailVerificationFailed());
            return;
        }
        yield put(influencerActions.resendEmailVerificationSuccess(result));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.resendEmailVerificationFailed(ex));
    }
}

function* disconnectSocialChannel(action) {
    const {channel, severConnection = false} = action.payload || {};
    try {
        const resp = yield call(InfluencerApi.disconnectSocialChannels, action.payload);
        if (resp.success) {
            yield put(
                influencerActions.disconnectSocialChannelSuccess({
                    ...resp.data,
                    severConnection
                })
            );
        }
    } catch (ex) {
        if (ex.response && ex.response.status === 404) {
            // user click cancel in oauth dialog, then click disconnect, this will throw 404 as it is not saved
            // ideally we should not send request, but for easy fix, treat 404 as disconnected
            yield put(
                influencerActions.disconnectSocialChannelSuccess({
                    platform: channel.platform,
                    disconnected: true
                })
            );
        } else {
            yield handleSagaError(ex, influencerActions.disconnectSocialChannelFailed(ex));
        }
    }
}

function* acceptAgreement(action) {
    try {
        yield put(errorActions.hideErrorMessage());
        const result = yield call(InfluencerApi.acceptAgreement, action.payload);
        yield put(influencerActions.acceptAgreementSuccess(result));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.acceptAgreementFailed(ex));
    }
}

function* closeAccount(action) {
    try {
        yield put(errorActions.hideErrorMessage());
        const result = yield call(InfluencerApi.closeAccount, action.payload);
        yield put(influencerActions.closeAccountSuccess(result));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.closeAccountFailed(ex));
    }
}

function* getFinalPayout() {
    try {
        const response = yield call(InfluencerApi.getFinalPayout);
        yield put(influencerActions.getFinalPayoutSuccess(response));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.getFinalPayoutFailed(ex));
    }
}

function* updateOAuthChannel(action) {
    try {
        const data = yield call(InfluencerApi.updateOAuthChannel, action.payload);
        yield put(influencerActions.updateOAuthChannelSuccess(data));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.updateOAuthChannelFailed(ex));
    }
}

function* checkCodeAvailability(action) {
    try {
        const response = yield call(InfluencerApi.checkCodeAvailability, action.payload);
        yield put(influencerActions.checkCodeAvailabilitySuccess(response));
    } catch (ex) {
        yield handleSagaError(ex, influencerActions.checkCodeAvailabilityFailed(ex));
    }
}

function* influencerSaga() {
    yield takeLatest(influencerActions.TYPES.GET_INFLUENCER_PROFILE, getInfluencerProfile);
    yield takeLatest(influencerActions.TYPES.CREATE_INFLUENCER_PROFILE, createInfluencerProfile);
    yield takeLatest(
        influencerActions.TYPES.REACTIVATE_INFLUENCER_PROFILE,
        reactivateInfluencerProfile
    );
    yield takeLatest(influencerActions.TYPES.ACCEPT_AGREEMENT, acceptAgreement);
    yield takeLatest(influencerActions.TYPES.CLOSE_ACCOUNT, closeAccount);
    yield takeLatest(influencerActions.TYPES.RESEND_EMAIL_VERIFICATION, resendEmailVerification);
    yield takeLatest(influencerActions.TYPES.GET_AFFILIATE_SOCIAL_CHANNELS, getSocialChannels);
    yield takeLatest(influencerActions.TYPES.UPDATE_AFFILIATE_MAIN_CHANNEL, updateMainChannel);
    yield takeLatest(
        influencerActions.TYPES.DISCONNECT_AFFILIATE_SOCIAL_CHANNEL,
        disconnectSocialChannel
    );
    yield takeLatest(influencerActions.TYPES.GET_FINAL_PAYOUT, getFinalPayout);
    yield takeLatest(influencerActions.TYPES.UPDATE_OAUTH_CHANNEL, updateOAuthChannel);
    yield takeLatest(influencerActions.TYPES.CHECK_CODE_AVAILABILITY, checkCodeAvailability);
}

export default influencerSaga;
